import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, RefreshControl, Vibration, View } from 'react-native';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import { useNavigation, useRoute } from '@react-navigation/native';
import Toast from 'react-native-root-toast';

import ServiceNoteButton from './components/ServiceNoteButton';
import { MACHINE_SERVICE_TYPES, POS_TYPE, ROLE_PERMISSIONS } from '../../../common/constants';
import { DialogContext, useAppContext, useUserContext } from '../../../common/utils/context';
import { useDidUpdateEffect, usePrevious } from '../../../common/utils/hooks';
import { getFullMachineName } from '../../../common/utils/machine';
import { shouldMachineBeRestocked } from '../../../common/utils/serviceRuns/shouldMachineBeRestocked';
import { getLocalTime } from '../../../common/utils/time/time';
import { analyseCoinMech } from '../../../common/utils/vending-utils';
import Text from '../../components/text/Text';
import {
	ActionDock,
	ListRow,
	Screen,
	SecondaryButton,
	SectionTitle,
	Sheet,
	Tag,
	WarningBar,
} from '../../design/components';
import {
	CLEAR_SERVICE_DOCKET_COULD_NOT_COMPLETE,
	GET_STOP_EXTRAS,
	MARK_SERVICE_DOCKET_COULD_NOT_COMPLETE,
	MODIFY_SERVICE_DOCKET,
} from '../../graphql/serviceDocket';
import withMediaPicker from '../../hocs/withMediaPicker';
import { useRefreshPage } from '../../hooks';
import useBackLabel from '../../hooks/useBackLabel';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useOfflineMutation, useOfflineQueue } from '../../offline';
import { getStopProductChanges } from '../../utils/getStopProductChanges';
import DocketCashBagDetail from '../docket/DocketCashBagDetail';
import DocketTubDetail from '../docket/DocketTubDetail';
import { canShowDocketCoinMech, openDocketCoinMech } from '../docket/docketDetails/DocketCoinMechDetail';
import { ManualRestockDialog } from '../manualRestock';
import { SUBSCRIBE_TO_SERVICE_DOCKET_MY_DAY } from '../myDay/driverMyDayQueries';
import { isMyDayTraining } from '../myDay/myDayTraining';
import TrainingModeBanner from '../myDay/TrainingModeBanner';
import { CouldNotCompleteCard, CouldNotCompleteSheet } from './components/CouldNotComplete';
import FreshFoodCard from './components/FreshFoodCard';
import ProductChangesWarning from './components/ProductChangesWarning';
import StopPlaceHeader from './components/StopPlaceHeader';
import StopQuickActions from './components/StopQuickActions';
import StopStepRow from './components/StopStepRow';
import StopSyncNote from './components/StopSyncNote';
import StopVehicleDialog from './components/StopVehicleDialog';
import { useDriverDay, useDriverShell } from './DriverDayProvider';
import DriverTrainingBanner from './DriverTrainingBanner';
import { DRIVER_ROUTE } from './driverRoutes';
import {
	getStopImportantNotes,
	getStopPositionName,
	getStopSiteName,
	getStopSyncState,
	getStopWarnings,
} from './stopDetails';
import { createStopViewStyles } from './StopView.styles';
import useStopActions from './useStopActions';
import useStopDocket from './useStopDocket';

const STEP_ACTION_LABEL = {
	pick: 'Open pick list',
	restock: 'Restock',
	'confirm_values': 'Confirm values',
	'confirm_stock': 'Confirm stock levels',
	tickets: 'Resolve tickets',
	photos: 'Take photos',
};

const getNextStepAction = (step) => {
	if (step.id === 'restock' && step.isWaiting) {
		return 'Check restock status';
	}

	return STEP_ACTION_LABEL[step.id] || step.label;
};

/**
 * Stop screen for any stop. Route params are the same as DocketDetailView's
 * (getDocketNavigationParams): { docketId (required), machineId, machineName,
 * machineType, isMarket, myDayTraining? }. Stops of the driver's own day come
 * from the shared driver day (live, offline); any other stop (another day or
 * driver, or the day failed to load) is loaded by id (useStopDocket) and gets
 * the same stop actions as the classic stop screen, without the driver-day
 * extras (Up next, skips, stop number).
 * Top: place, machine, warnings, steps. Bottom: quick actions, then the
 * pinned primary button that follows the next step (SPEC decision 9).
 */
const StopViewRaw = ({ captureMedia, capturedMediaBatch }) => {
	const navigation = useNavigation();
	const route = useRoute();
	const styles = useThemedStyles(createStopViewStyles);
	const { user, tenant, tenantSettings, hasPermission, isSuperAdmin } = useUserContext();
	const { disableSubscriptions } = useAppContext();
	const shell = useDriverShell();
	const backLabel = useBackLabel();
	const day = useDriverDay();
	const { queue, isOnline } = useOfflineQueue();
	const docketId = route.params?.docketId;
	const { docket, isInDriverDay, isLoading, error, refetch } = useStopDocket(day, docketId);
	// Training belongs to the training run's stops only; another run's stop
	// opened during training is a normal admin visit (as useDriverDayActions).
	// A stop opened as a training stop (route param, as DocketDetailView) stays
	// one even after it leaves the loaded driver day (e.g. reassigned).
	const routeTrainingContext = isMyDayTraining(route.params?.myDayTraining) ? route.params.myDayTraining : null;
	const dayTrainingContext = isInDriverDay ? day.trainingContext : null;
	const trainingContext = dayTrainingContext || routeTrainingContext;
	const isTraining = isMyDayTraining(trainingContext);
	const scopedQueue = useMemo(() => (isTraining ? [] : queue), [isTraining, queue]);

	const { data: extrasData, refetch: refetchExtras } = useQuery(GET_STOP_EXTRAS, {
		variables: { id: docketId },
		skip: !docketId,
		fetchPolicy: isTraining ? 'no-cache' : 'cache-and-network',
		errorPolicy: 'all',
	});
	const extras = extrasData?.getServiceDocket || null;
	// The driver day's own subscription keeps its stops live; other stops subscribe here.
	useSubscription(SUBSCRIBE_TO_SERVICE_DOCKET_MY_DAY, {
		variables: { input: {} },
		skip: !docketId || isInDriverDay || isTraining || Boolean(disableSubscriptions || user?.settings?.performanceMode),
	});

	const serviceRun = extras?.serviceRun || docket?.serviceRun || (isInDriverDay ? day.serviceRun : null);
	const planned = docket?.serviceRun?.planned || serviceRun?.planned;
	const refetchStop = useCallback(() => Promise.all([
		isInDriverDay ? day.refetchRuns() : refetch(),
		refetchExtras(),
	]), [day, isInDriverDay, refetch, refetchExtras]);
	const [refreshPage, isRefreshing] = useRefreshPage(refetchStop);

	const stopActions = useStopActions({
		docket,
		planned,
		trainingContext,
		onChanged: refetchStop,
		captureMedia,
		capturedMediaBatch,
		captureBeforeOpenPhotos: true,
	});

	// A stop finished outside its steps (e.g. completed in the office) reads as
	// done everywhere, as on the run list: every step done, dock moves on.
	const isStopDone = Boolean(docket) && day.isDocketDone(docket);
	const steps = useMemo(() => {
		const builtSteps = day.buildStopSteps(docket);
		return isStopDone
			? builtSteps.map(step => (step.required === false ? step : { ...step, done: true, blocked: false, isWaiting: false }))
			: builtSteps;
	}, [day, docket, isStopDone]);
	const nextStep = steps.find(step => step.required !== false && !step.done && !step.blocked) || null;
	const waitingStep = !nextStep ? steps.find(step => step.required !== false && !step.done && step.blocked) : null;
	const requiredSteps = steps.filter(step => step.required !== false);
	const doneStepCount = requiredSteps.filter(step => step.done).length;
	const syncState = useMemo(() => getStopSyncState(scopedQueue, docket), [docket, scopedQueue]);
	const stopQueueEntries = useMemo(() => scopedQueue.filter(entry => entry.meta?.serviceDocketId === docket?.id), [docket?.id, scopedQueue]);
	const pendingSyncKeys = useMemo(() => new Set(stopQueueEntries.map(entry => entry.configKey)), [stopQueueEntries]);
	const failedSyncKeys = useMemo(() => new Set(stopQueueEntries
		.filter(entry => entry.failureCount > 0)
		.map(entry => entry.configKey)), [stopQueueEntries]);

	// Feel the DEX (or manual) restock land while standing at the machine.
	const isRestocked = Boolean(docket?.isRestocked);
	const wasRestocked = usePrevious(isRestocked);
	useDidUpdateEffect(() => {
		if (isRestocked && wasRestocked === false) {
			Vibration.vibrate([0, 100]);
		}
	}, [isRestocked, wasRestocked]);

	const stopIndex = isInDriverDay ? day.sortedDockets.findIndex(item => item.id === docket.id) : -1;
	const upcomingDocket = useMemo(() => {
		if (stopIndex < 0) {
			return null;
		}

		return [
			...day.sortedDockets.slice(stopIndex + 1),
			...day.sortedDockets.slice(0, stopIndex),
		].find(item => !day.isDocketHandled(item)) || null;
	}, [day, stopIndex]);

	const goToStop = useCallback((targetDocket) => {
		day.setSelectedDocketId(targetDocket.id);
		navigation.replace(DRIVER_ROUTE.STOP, day.getDocketNavigationParams(targetDocket));
	}, [day, navigation]);

	// "Couldn't complete this machine": the driver gives a reason and moves on.
	// Nothing waits for it; the stop can still be completed later. Queued offline.
	const [isCouldNotCompleteOpen, setIsCouldNotCompleteOpen] = useState(false);
	const [markCouldNotCompleteMutation, { loading: isMarkingCouldNotComplete }] = useOfflineMutation(MARK_SERVICE_DOCKET_COULD_NOT_COMPLETE, {
		configKey: 'markServiceDocketCouldNotComplete',
	});
	const [clearCouldNotCompleteMutation, { loading: isClearingCouldNotComplete }] = useOfflineMutation(CLEAR_SERVICE_DOCKET_COULD_NOT_COMPLETE, {
		configKey: 'clearServiceDocketCouldNotComplete',
	});
	const offlineMeta = useMemo(() => ({
		machineName: getFullMachineName(docket?.machine, docket?.machine?.place, docket?.machine?.location),
	}), [docket?.machine]);
	const markCouldNotComplete = useCallback(async (reason) => {
		try {
			const result = await markCouldNotCompleteMutation({
				variables: { serviceDocketId: docket.id, reason },
				context: { offlineMeta },
			});
			if (result?.offline) {
				Toast.show('Saved on phone, will sync');
			}
		}
		catch (mutationError) {
			Toast.show(mutationError?.message || 'Unable to save. Try again.');
			return;
		}

		setIsCouldNotCompleteOpen(false);
		if (upcomingDocket) {
			goToStop(upcomingDocket);
		}
		else {
			navigation.navigate(shell.isMyDayEnabled ? DRIVER_ROUTE.END_OF_DAY : DRIVER_ROUTE.RUN_LIST);
		}
	}, [docket?.id, goToStop, markCouldNotCompleteMutation, navigation, offlineMeta, shell.isMyDayEnabled, upcomingDocket]);
	const undoCouldNotComplete = useCallback(async () => {
		try {
			await clearCouldNotCompleteMutation({
				variables: { serviceDocketId: docket.id },
				context: { offlineMeta },
			});
		}
		catch (mutationError) {
			Toast.show(mutationError?.message || 'Unable to undo. Try again.');
		}
	}, [clearCouldNotCompleteMutation, docket?.id, offlineMeta]);

	// Cash bag and tub are edited online, as on the classic stop screen.
	const [cashBagDialog, setCashBagDialog] = useState(false);
	const [tubDialog, setTubDialog] = useState(false);
	// The editors close through DialogContext after saving (useDialogSaveButton), as
	// inside a Dialog; a failed save shows a toast and closes too, and reopening
	// starts a fresh editor, so the driver can retry.
	const cashBagSheetContext = useMemo(() => ({ closeDialog: () => setCashBagDialog(false) }), []);
	const tubSheetContext = useMemo(() => ({ closeDialog: () => setTubDialog(false) }), []);
	const [modifyServiceDocketMutation] = useMutation(MODIFY_SERVICE_DOCKET);
	const modifyServiceDocket = useCallback(async (values) => {
		if (isTraining && !isOnline) {
			Toast.show('Reconnect to continue training');
			return;
		}

		try {
			await modifyServiceDocketMutation({
				variables: {
					input: {
						id: docket.id,
						...values,
						...(isTraining ? { adminTraining: true } : {}),
					},
				},
			});
			setCashBagDialog(false);
			setTubDialog(false);
		}
		catch (mutationError) {
			Toast.show(mutationError?.message || 'Unable to save the change');
		}
	}, [docket?.id, isOnline, isTraining, modifyServiceDocketMutation]);

	// Vehicle assignment prompt after the restock, as on the classic stop screen.
	const hasAssignedVehicle = Boolean(serviceRun?.vehicle?.id);
	const needsVehicle = !hasAssignedVehicle || !serviceRun?.vehicleConfirmed;
	const isPlannedToday = Boolean(planned) && getLocalTime(tenant?.timezone, planned).isSame(getLocalTime(tenant?.timezone), 'day');
	const shouldAskForVehicle = Boolean(extras && tenantSettings?.showVehicleAssignmentDialog && isPlannedToday && needsVehicle && isRestocked);
	const [isVehicleDialogOpen, setIsVehicleDialogOpen] = useState(false);
	const askedVehicleForDocketRef = useRef(null);
	useEffect(() => {
		if (shouldAskForVehicle && askedVehicleForDocketRef.current !== docket?.id) {
			askedVehicleForDocketRef.current = docket?.id;
			setIsVehicleDialogOpen(true);
		}
	}, [docket?.id, shouldAskForVehicle]);

	if (!docket) {
		return (
			<Screen
				headerRight={<ServiceNoteButton />}
				backLabel={backLabel}
				heading={route.params?.machineName}
				isLoading={isLoading}
				error={error ? 'This stop could not be loaded. Check your connection and try again.' : undefined}
				onRetry={() => refetch()}
			>
				<Text style={styles.secondaryText}>This stop may have been removed from its run.</Text>
			</Screen>
		);
	}

	const { machine } = docket;
	const isMarket = machine?.type === POS_TYPE.MARKET;
	const isMachineOffline = !machine?.commDevices?.length;
	const canCreateTicket = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_TICKETS);
	const canSeeMachineDetail = hasPermission(ROLE_PERMISSIONS.VIEW_MACHINE_DETAIL_PAGE);
	const showCoinMech = canShowDocketCoinMech(machine, hasPermission(ROLE_PERMISSIONS.VIEW_MACHINE_COIN_MECH));
	const analysedCoinMech = showCoinMech
		? analyseCoinMech(machine.coinMechDetail?.tubesDetail, machine.coinMech, tenantSettings, machine)
		: null;
	// Per-machine cash bags unless the run uses one bag (older runs have no mode set);
	// the editor stays hidden until the run details load.
	const showCashBag = Boolean(docket.collectCash && serviceRun && !serviceRun.isOneCashBag);
	const showTub = Boolean(tenantSettings?.enableTubsFieldOnDockets);
	// Same rule as the classic stop screen's "Correct delivered quantities".
	const hasPendingInventorySync = scopedQueue.some(entry => (
		['manualRestock', 'confirmStockLevels', 'confirmMachineValues'].includes(entry.configKey) &&
		(entry.meta?.machineId === machine?.id || entry.meta?.serviceDocketId === docket.id)
	));
	const canCorrectMarketRestock = isOnline && isMarket && !isMachineOffline &&
		docket.isRestocked && docket.manualRestock && !docket.isIngredientRestock &&
		extras?.marketRestockCorrectionAvailability?.allowed &&
		!hasPendingInventorySync &&
		((user?.id && serviceRun?.driver?.id === user.id) || isSuperAdmin || hasPermission(ROLE_PERMISSIONS.ADD_EDIT_SERVICE_RUNS));
	const needsExpiryCheck = Boolean(docket.productExpiryCheckSchedules?.length || docket.hasSomeExpiryDatesToUpdate);
	const canSkipLocally = isInDriverDay && !isTraining;
	// A completed stop is simply complete, even if it was marked before.
	const couldNotComplete = !isStopDone && docket.couldNotComplete?.at ? docket.couldNotComplete : null;
	const canMarkCouldNotComplete = canSkipLocally && !isStopDone && !couldNotComplete;
	const couldNotCompleteTitle = isMarket ? 'Couldn\'t complete this market' : 'Couldn\'t complete this machine';
	// Stops without a pick step (offline machines, label-only packs) still have a
	// pick list; drivers can view it here without editing.
	const showPickListView = Boolean(docket.pickList?.id) && !steps.some(step => step.id === 'pick');
	// Fresh food comes from the driver day (the active run's stops only).
	const freshFoodStop = isInDriverDay ? day.freshFoodStops.find(stop => stop.serviceDocketId === docket.id) : null;

	const notNeeded = [
		!shouldMachineBeRestocked(docket.machineServiceType) && 'Restock',
		!steps.some(step => step.id === 'pick') && !showPickListView && 'Pick list',
		!steps.some(step => step.id === 'tickets') && 'Tickets',
		!docket.requirePhoto && 'Photos',
		// One-bag runs collect cash into the run's bag, so the stop still needs cash.
		!docket.collectCash && 'Cash bag',
		!showCoinMech && 'Coin mech',
	].filter(Boolean);

	// The step pages show "After this: <next step>"; Photos also says when a photo is still required.
	const openStep = step => stopActions.onStepPress(step, {
		nextStepLabel: steps.find(item => item.required !== false && !item.done && item.id !== step.id)?.label,
		...(step.id === 'photos' ? { photoRequired: !step.done } : {}),
	});

	const renderSkip = () => {
		if (!canSkipLocally || !nextStep) {
			return null;
		}

		if (nextStep.id === 'pick') {
			return (
				<View style={styles.skip}>
					<SecondaryButton label="Skip pick list for now" variant="tonal" onPress={() => day.skipPickStepForDocket(docket.id)} />
					<Text style={styles.secondaryText}>This only skips the step here and does not change pick list quantities.</Text>
				</View>
			);
		}

		if (nextStep.id === 'restock' && nextStep.canSkipOffline) {
			return (
				<View style={styles.skip}>
					<SecondaryButton label="Continue without waiting" variant="tonal" onPress={() => day.skipRestockStepForDocket(docket.id)} />
					<Text style={styles.secondaryText}>This only skips the step here. The restock still records when connection returns.</Text>
				</View>
			);
		}

		return null;
	};

	const getDockAction = () => {
		if (nextStep) {
			return { label: getNextStepAction(nextStep), onPress: () => openStep(nextStep) };
		}
		if (waitingStep) {
			return { label: 'Waiting for restock', disabled: true };
		}
		if (!isInDriverDay) {
			return null;
		}
		if (upcomingDocket) {
			return { label: 'Next stop', onPress: () => goToStop(upcomingDocket) };
		}
		if (shell.isMyDayEnabled && day.allStopsHandled) {
			return { label: 'Go to end of day', onPress: () => navigation.navigate(DRIVER_ROUTE.END_OF_DAY) };
		}

		return { label: 'Back to run', onPress: () => navigation.navigate(DRIVER_ROUTE.RUN_LIST) };
	};
	const dockAction = getDockAction();
	const upNextText = upcomingDocket
		? `Up next: ${[getStopSiteName(upcomingDocket), getStopPositionName(upcomingDocket)].filter(Boolean).join(' · ')}`
		: undefined;

	const productChangeCount = getStopProductChanges(docket).length;
	const warnings = getStopWarnings(docket);
	const importantNotes = getStopImportantNotes(docket);

	let banner = null;
	if (isTraining) {
		banner = dayTrainingContext
			? <DriverTrainingBanner />
			: <TrainingModeBanner trainingContext={trainingContext} isOnline={isOnline} />;
	}

	return (
		<Screen
			headerRight={<ServiceNoteButton />}
			title={stopIndex >= 0
				? `Stop ${stopIndex + 1} of ${day.sortedDockets.length}`
				: (planned ? getLocalTime(tenant?.timezone, planned).format('ddd D MMM') : undefined)}
			backLabel={backLabel}
			headerProgress={isInDriverDay && day.sortedDockets.length ? day.completedStopsCount / day.sortedDockets.length : undefined}
			refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={refreshPage} />}
			banner={(
				<>
					{banner}
					<StopSyncNote pending={syncState.pending} failed={syncState.failed} isOnline={isOnline} />
				</>
			)}
			dock={dockAction ? <ActionDock hint={upNextText} action={dockAction} /> : undefined}
		>
			<StopPlaceHeader docket={docket} tenantSettings={tenantSettings} showCashBag={showCashBag} showTub={showTub} />

			{Boolean(couldNotComplete) && (
				<CouldNotCompleteCard
					couldNotComplete={couldNotComplete}
					timezone={tenant?.timezone}
					// Training must not clear the driver's mark; a queued change waits for sync so Undo can't land before it.
					onUndo={isTraining || syncState.pending.length || syncState.failed.length ? undefined : undoCouldNotComplete}
					isUndoing={isClearingCouldNotComplete}
				/>
			)}

			<WarningBar items={warnings} />
			<WarningBar items={importantNotes} tone="danger" />

			{Boolean(freshFoodStop) && (
				<FreshFoodCard
					stop={freshFoodStop}
					onPress={docket.pickList?.id ? () => stopActions.openPickList(docket, showPickListView ? { readOnly: true } : undefined) : undefined}
				/>
			)}

			{(steps.length > 0 || productChangeCount > 0) && (
				<View style={styles.section}>
					<SectionTitle title="This stop" meta={`${doneStepCount} of ${requiredSteps.length} required ${requiredSteps.length === 1 ? 'step' : 'steps'} done`} />
					<ProductChangesWarning docketId={docket.id} productChangeCount={productChangeCount} />
					{steps.map(step => (
						<StopStepRow
							key={step.id}
							step={step}
							nextStepId={nextStep?.id}
							pendingSyncKeys={pendingSyncKeys}
							failedSyncKeys={failedSyncKeys}
							isRepair={docket.machineServiceType === MACHINE_SERVICE_TYPES.REPAIR}
							needsExpiryCheck={needsExpiryCheck}
							onPress={openStep}
						/>
					))}
					{renderSkip()}
				</View>
			)}

			{(showPickListView || showCoinMech || showCashBag || showTub || canCorrectMarketRestock || canMarkCouldNotComplete) && (
				<View style={styles.section}>
					{showPickListView && (
						<ListRow
							icon="list"
							title="Pick list"
							subtitle="View only"
							onPress={() => stopActions.openPickList(docket, { readOnly: true })}
						/>
					)}
					{showCoinMech && (
						<ListRow
							icon="disc"
							title="Coin mech"
							subtitle={analysedCoinMech.label}
							right={analysedCoinMech.mechRequiresFill ? <Tag label="Coins required" tone="warning" /> : undefined}
							onPress={() => openDocketCoinMech(navigation, machine, analysedCoinMech, docket.id)}
						/>
					)}
					{showCashBag && (
						<ListRow
							icon="briefcase"
							title="Cash bag"
							subtitle={docket.cashBag || 'No cash bag set'}
							onPress={() => setCashBagDialog(true)}
						/>
					)}
					{showTub && (
						<ListRow
							icon="archive"
							title="Tub"
							subtitle={docket.tub || 'No tub'}
							onPress={() => setTubDialog(true)}
						/>
					)}
					{canCorrectMarketRestock && (
						<ListRow
							icon="edit-3"
							title="Correct delivered quantities"
							subtitle="Fix what was delivered in this restock"
							onPress={() => navigation.push('CorrectMarketRestockView', {
								docketId: docket.id,
								machineName: getFullMachineName(machine, machine?.place, machine?.location),
								...stopActions.trainingRouteParams,
							})}
						/>
					)}
					{canMarkCouldNotComplete && (
						<ListRow
							icon="x-circle"
							title={couldNotCompleteTitle}
							subtitle="Give a reason and move on to the next stop"
							onPress={() => setIsCouldNotCompleteOpen(true)}
						/>
					)}
				</View>
			)}

			{notNeeded.length > 0 && (
				<Text style={styles.secondaryText}>{`Not needed at this stop: ${notNeeded.join(', ')}`}</Text>
			)}

			<StopQuickActions
				docket={docket}
				isMarket={isMarket}
				canCreateTicket={canCreateTicket}
				canSeeMachineDetail={canSeeMachineDetail}
				stopActions={stopActions}
			/>

			<ManualRestockDialog
				isOpen={stopActions.manualRestock.isOpen}
				setIsOpen={stopActions.manualRestock.setIsOpen}
				onClick={stopActions.manualRestock.submit}
				values={stopActions.manualRestock.values}
				changeFormValue={stopActions.manualRestock.changeFormValue}
			/>
			{canMarkCouldNotComplete && (
				<CouldNotCompleteSheet
					isVisible={isCouldNotCompleteOpen}
					onClose={() => setIsCouldNotCompleteOpen(false)}
					title={couldNotCompleteTitle}
					onSubmit={markCouldNotComplete}
					isSubmitting={isMarkingCouldNotComplete}
				/>
			)}
			<Sheet isVisible={cashBagDialog} onClose={() => setCashBagDialog(false)} title="Cash bag">
				<DialogContext.Provider value={cashBagSheetContext}>
					<DocketCashBagDetail
						cashBag={docket.cashBag}
						modifyServiceDocket={modifyServiceDocket}
						disabled={isTraining && !isOnline}
						showTitle={false}
					/>
				</DialogContext.Provider>
			</Sheet>
			<Sheet isVisible={tubDialog} onClose={() => setTubDialog(false)} title="Tub">
				<DialogContext.Provider value={tubSheetContext}>
					<DocketTubDetail
						tub={docket.tub}
						modifyServiceDocket={modifyServiceDocket}
						disabled={isTraining && !isOnline}
						showTitle={false}
					/>
				</DialogContext.Provider>
			</Sheet>
			{Boolean(serviceRun) && (
				<StopVehicleDialog
					serviceRun={serviceRun}
					isOpen={isVehicleDialogOpen}
					setIsOpen={setIsVehicleDialogOpen}
					canClose={isSuperAdmin || (hasAssignedVehicle && Boolean(serviceRun.vehicleConfirmed))}
					onUpdated={refetchStop}
					isTraining={isTraining}
					isOnline={isOnline}
				/>
			)}
		</Screen>
	);
};

const StopViewWithMediaPicker = withMediaPicker(StopViewRaw);

const StopView = () => {
	const route = useRoute();
	const isMarket = typeof route.params?.isMarket === 'boolean'
		? route.params.isMarket
		: route.params?.machineType === POS_TYPE.MARKET;

	return (
		<StopViewWithMediaPicker
			fallbackToLocalUri={true}
			quickMediaCapture={true}
			collectCapturedMediaBatch={true}
			allowMultipleCapture={isMarket}
			// react-native-web's Alert is a no-op, so the "Take another photo?" prompt never resolves there
			// and nothing is saved; on web each capture saves one photo, as the classic board did.
			promptForAnotherCapture={isMarket && Platform.OS !== 'web'}
		/>
	);
};

export default StopView;
